import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { CForm, CButton, P } from '@appcues/component-library';
import { Select, RadioGroup, Radio, StatusLabel } from 'next/components/ui';
import { JOB_DROPDOWN_OPTIONS } from 'constants/inviteModal';
import Modal from 'components/Common/UI/Modal/Modal';
import UpgradeTag from 'components/Common/UpgradeTag';
import { BeaconLink } from 'components/Common/HelpScout';

const RadioOptionWrapper = styled.div`
  display: flex;
  align-items: end;
  ${StatusLabel} {
    margin-left: 8px;
    margin-bottom: 12px;
  }
`;

const InviteModal = ({ enableEditorRole, onSubmit, onClose }) => {
  const [inFlight, setInFlight] = useState(false);

  const permisisonOptions = useMemo(
    () => [
      {
        label: 'Admin',
        value: 'ACCOUNT_ADMIN',
        caption: 'Create, edit, publish, and manage account',
      },
      {
        label: 'Publisher',
        value: 'PUBLISHER',
        caption: 'Create, edit, and publish',
      },
      {
        label: 'Editor',
        value: 'EDITOR',
        caption: 'Create and edit (cannot publish)',
        disabled: !enableEditorRole,
      },
    ],
    [enableEditorRole]
  );

  const handleSubmit = ({
    firstName,
    lastName,
    emailAddress: email,
    job: { value: job },
    role,
  }) => {
    setInFlight(true);
    onSubmit({
      name: `${firstName} ${lastName}`,
      email,
      job,
      role,
    }).finally(() => setInFlight(false));
  };

  return (
    <Modal type="full" title="Invite a teammate" onClose={onClose}>
      <StyledForm
        onSubmit={handleSubmit}
        initialValues={{
          firstName: '',
          lastName: '',
          emailAddress: '',
          job: '',
          role: '',
        }}
        validationSchema={yup.object().shape({
          job: yup.string().required('Required'),
          lastName: yup.string().required('Required'),
          firstName: yup.string().required('Required'),
          emailAddress: yup
            .string()
            .email('Email must be a valid address.')
            .required('Required'),
          role: yup.string().required('Required'),
        })}
      >
        <Modal.Content paddingX={20}>
          <P>We&apos;ll send them an email invite to your Appcues account.</P>
          <CForm.Row>
            <CForm.Field
              label="First Name"
              placeholder="Enter first name"
              name="firstName"
              disabled={inFlight}
            />
            <CForm.Field
              label="Last Name"
              placeholder="Enter last name"
              name="lastName"
              disabled={inFlight}
            />
          </CForm.Row>
          <CForm.Row>
            <CForm.Field
              label="Email"
              placeholder="Enter email address"
              name="emailAddress"
              disabled={inFlight}
            />
            <CForm.Field label="Job" name="job">
              <Select
                aria-label="Job select"
                isDisabled={inFlight}
                options={JOB_DROPDOWN_OPTIONS}
                placeholder="--- Please select ---"
              />
            </CForm.Field>
          </CForm.Row>
          <CForm.Field label="Permissions" name="role">
            <RadioGroup isDisabled={inFlight}>
              {permisisonOptions.map(({ label, value, caption, disabled }) => (
                <RadioOptionWrapper>
                  <Radio key={value} value={value} disabled={disabled}>
                    {label}
                    <div>{caption}</div>
                  </Radio>
                  {/* TODO: UpgradeTag should be together with the caption inside of Radio, move it once we upgrade Radio to Radix */}
                  {disabled && (
                    <UpgradeTag
                      tooltipContent={
                        <>
                          <BeaconLink
                            aria-label="Upgrade to get access to editor role and customize your team access."
                            message="I'd like to enable the editor permissions role."
                            subject="Interested in editor permissions role"
                          >
                            Upgrade{' '}
                          </BeaconLink>
                          to get access to editor role and customize your team
                          access.
                        </>
                      }
                    />
                  )}
                </RadioOptionWrapper>
              ))}
            </RadioGroup>
          </CForm.Field>
        </Modal.Content>
        <Modal.Footer>
          <CButton onClick={onClose} disabled={inFlight}>
            Cancel
          </CButton>
          <CForm.SubmitButton
            type="primary"
            disabled={inFlight}
            disabledUntilDirty={false}
          >
            Invite
          </CForm.SubmitButton>
        </Modal.Footer>
      </StyledForm>
    </Modal>
  );
};

const StyledForm = styled(CForm)`
  height: 100%;
  width: 100%;
  main {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    display: block;
  }
`;

InviteModal.propTypes = {
  enableEditorRole: PropTypes.bool,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func,
};

export default InviteModal;
